/**
 * UI-level PII masking for DPDP compliance.
 * Masking is display-only; raw values must still be used for API/OTP flows.
 */
(function (global) {
  const access = global.PII_ACCESS || {};

  function shouldMaskPII() {
    if (!access.viewerLoggedIn) return false;
    if (access.isAuthorizedViewer) return false;
    return !!(access.isSalesRep || access.isInternalDomainViewer);
  }

  function maskEmail(email) {
    if (!email || typeof email !== 'string') return email || '';
    const trimmed = email.trim();
    const atIndex = trimmed.indexOf('@');
    if (atIndex < 1) return trimmed;
    const local = trimmed.slice(0, atIndex);
    const domain = trimmed.slice(atIndex);
    const visibleLen = local.length <= 3 ? Math.min(2, local.length) : 3;
    return local.slice(0, visibleLen) + '****' + domain;
  }

  function maskPhone(phone) {
    if (!phone || typeof phone !== 'string') return phone || '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length <= 4) return '****';
    const prefix = digits.slice(0, 2);
    const suffix = digits.slice(-2);
    const maskedMiddle = '*'.repeat(Math.max(4, digits.length - 4));
    return prefix + maskedMiddle + suffix;
  }

  function maskName(name) {
    if (!name || typeof name !== 'string') return name || '';
    return name
      .trim()
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part.charAt(0).toUpperCase() + '***')
      .join(' ');
  }

  function maskStreetLine() {
    return '*** Street';
  }

  /**
   * Build masked address lines for display.
   * Hides house/street; shows city/state and country.
   */
  function buildMaskedAddressLines({
    firstName,
    lastName,
    address1,
    address2,
    city,
    province,
    zip,
    country,
    phone,
  } = {}) {
    const lines = [];
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fullName) lines.push(maskName(fullName));

    const locationParts = [city, province, zip].filter(Boolean);
    const locationLine = locationParts.join(', ');
    if (locationLine) {
      lines.push(maskStreetLine() + (city ? ', ' + city : ''));
    } else if (address1 || address2) {
      lines.push(maskStreetLine());
    }

    if (country) lines.push(country);
    if (phone) lines.push(maskPhone(String(phone)));
    return lines;
  }

  /**
   * Build full address lines (unmasked).
   */
  function buildAddressLines({
    firstName,
    lastName,
    address1,
    address2,
    city,
    province,
    zip,
    country,
    phone,
  } = {}) {
    const lines = [];
    const fullName = [firstName, lastName].filter(Boolean).join(' ').trim();
    if (fullName) lines.push(fullName);
    if (address1) lines.push(address1);
    if (address2) lines.push(address2);
    if (city || province || zip) {
      lines.push([city, province, zip].filter(Boolean).join(', '));
    }
    if (country) lines.push(country);
    if (phone) lines.push(String(phone));
    return lines;
  }

  function buildAddressLinesForDisplay(address, mask) {
    const fn = mask ? buildMaskedAddressLines : buildAddressLines;
    return fn(address || {});
  }

  function maskPaymentDetail(value, type) {
    if (!value || typeof value !== 'string') return value || '';
    const trimmed = value.trim();
    if (!trimmed) return '';

    if (type === 'upi' || trimmed.includes('@')) {
      return maskEmail(trimmed);
    }

    const digits = trimmed.replace(/\D/g, '');
    if (digits.length >= 4) {
      const last4 = digits.slice(-4);
      return '**** **** **** ' + last4;
    }

    if (trimmed.length <= 4) return '****';
    return trimmed.slice(0, 2) + '***' + trimmed.slice(-2);
  }

  global.PII_MASKING = {
    shouldMaskPII,
    maskEmail,
    maskPhone,
    maskName,
    maskStreetLine,
    buildMaskedAddressLines,
    buildAddressLines,
    buildAddressLinesForDisplay,
    maskPaymentDetail,
  };
})(typeof window !== 'undefined' ? window : globalThis);
